import * as React from 'react';
import { SelectOptionLink } from './select-option-link';
import { SelectOptionValue } from 'app/area/code-selector/types';
import { ElementLoader } from 'ui/common/element-loader/element-loader';
import { Breadcrumb } from 'ui/common/breadcrumb/breadcrumb';
import { keyCodeTypes } from 'ui/common/key-code-types';
import { HotKeys, KeyMap, Handlers } from 'ui/common/hotkeys';
import { FixedDocument } from 'app/components/fixed-document';
import styles from './fullscreen-code-selector.scss';
import { FullscreenModalHeader } from 'ui';
import { Switch } from 'ui/common/switch/switch';
import * as navigate from 'app/area/shared/navigation';

export const findNestedSelectOption = (current:SelectOptionValue, target:number):SelectOptionValue[] => {
  if (current.value === target) {
    return [current];
  } else if(current.descendants) {
    for(let i = 0; i < current.descendants.length; i ++) {
      const rtn = findNestedSelectOption(current.descendants[i], target);
      if (rtn) {
        return [current, ...rtn];
      }
    }
  }
  return null;
};

const updateState = (
    rootTemplate, options: SelectOptionValue[], 
    currentFolder?: number, 
    showAllOptionsWhenSingleFolderExists?: boolean,
    isToggledOn?: boolean) => {

  const rvb: SelectOptionValue = { ...rootTemplate, descendants:options };
  const breadcrumb = findNestedSelectOption(rvb, currentFolder) || [rvb];

  if (!!options && options.length === 1 && breadcrumb.length === 1 && showAllOptionsWhenSingleFolderExists) {
    const firstEl = options[0];
    if (!!firstEl.descendants && firstEl.descendants.length > 0) {
      return {
        levelOptions: firstEl.descendants,
        breadcrumb: [rvb, firstEl]
      };
    }
  }
  return {
    levelOptions: breadcrumb[breadcrumb.length-1].descendants || [],
    breadcrumb,
    options,
    showToggledOnOptions: isToggledOn
  };
};

export interface FullscreenCodeSelectorProps {
  title: string;
  options: SelectOptionValue[];
  fetching: boolean;
  onClose?: () => void;
  onOpen?: () => void;
  urlResolver: (value: number[]) => string | {pathname: string; search: string};
  textGoBack: string;
  infoMessage?: string;
  routeHandler?: (route: string | object)=> void;
  query?: {[key:string]: string};
  itemDropdownRender?: (item:SelectOptionValue) => React.ReactNode;
  itemLabelRender?: (item:SelectOptionValue) => React.ReactNode;
  defaultIcon?: React.ReactNode;
  showAllOptionsWhenSingleFolderExists?: boolean;
  selectedFolder?: number;
  selectedItem?: number;
  showToggleButton?: boolean;
  onToggle?: (toggledOn?: boolean, overrideRouteUrl?: boolean) => void;
  isToggledOn?: boolean;
  subtitleToggledOff?: string;
  subtitleToggledOn?: string;
  itemBookmark?: (item: SelectOptionValue) => React.ReactNode;
  textSwitchTooltip?: string;
}

export interface ExpandingCodeSelectorState {
  levelOptions: SelectOptionValue[];
  breadcrumb: SelectOptionValue[];
  options?: SelectOptionValue[];
  showToggledOnOptions?: boolean;
}

const rootValueOfBreadcrumb: SelectOptionValue = { value: 0, description: '' };
export class FullscreenCodeSelector extends React.Component<FullscreenCodeSelectorProps, ExpandingCodeSelectorState> {

  private keyMap: KeyMap;
  private handlers: Handlers;

  public static getDerivedStateFromProps(props:FullscreenCodeSelectorProps, state:ExpandingCodeSelectorState) {
    const { options = [], showAllOptionsWhenSingleFolderExists = true, isToggledOn} = props;

    if (state.options !== options) {
      return updateState(rootValueOfBreadcrumb, props.options, props.selectedFolder, showAllOptionsWhenSingleFolderExists, isToggledOn);
    }

    return null;
  }

  constructor(props: FullscreenCodeSelectorProps) {
    super(props);
    
    const { options, isToggledOn } = props;
    this.state = updateState(rootValueOfBreadcrumb, options, props.selectedFolder, props.showAllOptionsWhenSingleFolderExists, isToggledOn);

    this.keyMap = {
      closeOptions: [keyCodeTypes.Esc]
    };

    this.handlers = {
      closeOptions: () => this.props.onClose()
    };
  }

  public componentDidMount() {
    const {fetching, onOpen} = this.props;

    if(!fetching){
      onOpen && onOpen();
    }
  }

  private onExpand = (item: SelectOptionValue) => {
    this.setState({
      levelOptions: [...item.descendants],
      breadcrumb: [...this.state.breadcrumb, item]
    });
  };

  private onClickBreadcrumbItem = (item: SelectOptionValue) => {
    const newBreadcrumb = [...this.state.breadcrumb];

    while (newBreadcrumb[newBreadcrumb.length - 1].value !== item.value) {
      newBreadcrumb.pop();
    }

    this.setState({
      levelOptions: item.descendants,
      breadcrumb: newBreadcrumb
    } as ExpandingCodeSelectorState);

    navigate.toSelectSavedReports(item?.value);
  };

  /**
   * Override the default select linking since user shold not navigate back to the
   * selector
   */
  private selectItem = (item: number)=> {
    const {urlResolver, routeHandler} = this.props;
    routeHandler && routeHandler(urlResolver([item]));
  };

  private handleClickClose = () => {
    this.props.onClose && this.props.onClose();
  };

  private handleOnToggle = async() => {
    const { onToggle } = this.props;
    onToggle(!this.state.showToggledOnOptions);
  };

  public render() {
    const { breadcrumb, levelOptions, showToggledOnOptions} = this.state;
    const { urlResolver, fetching, title, infoMessage, routeHandler, itemDropdownRender, itemLabelRender, defaultIcon, 
      selectedItem, subtitleToggledOff, subtitleToggledOn, itemBookmark, showToggleButton, textSwitchTooltip } = this.props;

    const selectOptions = levelOptions && levelOptions.map((item) => {
      return <SelectOptionLink key={item.value}
        data={item}
        urlResolver={urlResolver}
        onExpand={this.onExpand}
        onClick={ routeHandler ? this.selectItem : undefined}
        dropdownItems={itemDropdownRender ? itemDropdownRender(item) : null}
        labelItems={itemLabelRender ? itemLabelRender(item) : null}
        defaultIcon={defaultIcon}
        selectedItem={selectedItem}
        itemBookmark={itemBookmark?.(item)}
      />;
      }
    );

    const breadcrumbElement = <Breadcrumb items={breadcrumb} onClickItem={this.onClickBreadcrumbItem} />;
    const showBreadcrumb = breadcrumb.length > 1;
    const overrideTitleStyle = showToggleButton ? styles.showToggleTitle : '';
    const titleElement = !!title && <label className={overrideTitleStyle}>{title}</label>;
    const hasSelectOptionsData = !!selectOptions[0]?.props.data;

    return (
      <FixedDocument>
        <HotKeys keyMap={this.keyMap} handlers={this.handlers}>

          <div className={styles.fullscreenCodeSelector} >
            <FullscreenModalHeader onClose={this.handleClickClose}>
              <div className={styles.navbarInfoMessage}>{infoMessage}</div>
            </FullscreenModalHeader>

            <div className={`container ${styles.modalWrapper}`}>
              <div className={`main-grid ${styles.selectCaseContainer}`}>
                <div className={styles.selectTitle}>
                    {showBreadcrumb ? breadcrumbElement : titleElement}
                    {showToggleButton && 
                      <Switch
                        className={styles.selectSwitch}
                        checked={showToggledOnOptions}
                        onChange={this.handleOnToggle}
                        textTooltip={textSwitchTooltip}
                      />}
                </div>
                <ul className="row select-options">
                  {fetching ? <ElementLoader /> : hasSelectOptionsData ? selectOptions : 
                    !showBreadcrumb && (showToggledOnOptions ? <span className={styles.emptyList}>{subtitleToggledOn}</span> 
                      : <span className={styles.emptyList}>{subtitleToggledOff}</span>)}
                </ul>
              </div>
            </div>

            <a className={styles.backgroundCloseBtn}
              onClick={this.handleClickClose}
              aria-label="Close dialog window"
            />

          </div>
        </HotKeys>
      </FixedDocument>
    );
  }
}
